import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import "@lovable.dev/cloud-auth-js/styles.css";
import { Logo } from "@/components/logo";
import { sendWelcomeEmail } from "@/lib/welcome-email.functions";

function safeNext(value: unknown): string {
  if (typeof value !== "string") return "/";
  // Same-origin relative paths only.
  if (!value.startsWith("/") || value.startsWith("//")) return "/";
  return value;
}

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: (search: Record<string, unknown>) => ({
    next: safeNext(search.next),
  }),
  head: () => ({
    meta: [
      { title: "Sign in — MG Content Labs" },
      {
        name: "description",
        content:
          "Sign in or create an MG Content Labs account to manage your enquiries and connect AI assistants to your account.",
      },
      { property: "og:title", content: "Sign in — MG Content Labs" },
      {
        property: "og:description",
        content: "Access your MG Content Labs account and connected AI assistants.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { next } = Route.useSearch();
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const returnTo = `${window.location.origin}${next}`;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    if (mode === "forgot") {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      setBusy(false);
      if (resetError) return setError(resetError.message);
      setNotice("Password reset link sent — check your inbox (and spam folder).");
      return;
    }

    if (mode === "signup") {
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: returnTo },
      });
      setBusy(false);
      if (signUpError) return setError(signUpError.message);
      if (signUpData.user && signUpData.user.email) {
        // Fire-and-forget: never block signup on the welcome email.
        sendWelcomeEmail({
          data: { userId: signUpData.user.id, email: signUpData.user.email },
        }).catch(() => {});
      }
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        window.location.href = next;
        return;
      }
      setNotice("Check your inbox to confirm your email, then come back here.");
      return;
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (signInError) return setError(signInError.message);
    window.location.href = next;
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: returnTo });
    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? "Google sign-in failed.");
      return;
    }
    if (result.redirected) return;
    window.location.href = next;
  }

  async function handleMicrosoft() {
    setBusy(true);
    setError(null);
    const result = await lovable.auth.signInWithOAuth("microsoft", { redirect_uri: returnTo });
    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? "Microsoft sign-in failed.");
      return;
    }
    if (result.redirected) return;
    window.location.href = next;
  }

  async function handleLovable() {
    setBusy(true);
    setError(null);
    const result = await lovable.auth.signInWithOAuth("lovable", { redirect_uri: returnTo });
    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? "Lovable sign-in failed.");
      return;
    }
    if (result.redirected) return;
    window.location.href = next;
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-brand-muted/40 px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-brand-deep/10 bg-white p-8 shadow-xl">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        <h1 className="text-center text-2xl font-semibold text-brand-deep">
          {mode === "signin" ? "Sign in" : mode === "signup" ? "Create your account" : "Reset your password"}
        </h1>
        <p className="mt-2 text-center text-sm text-brand-deep/60">
          {mode === "signin"
            ? "Access your enquiries and connected AI assistants."
            : mode === "signup"
              ? "Set up an account to manage enquiries and integrations."
              : "Enter your account email and we'll send you a reset link."}
        </p>

        {error && (
          <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        {notice && (
          <p className="mt-4 rounded-md border border-brand-accent/30 bg-brand-accent/5 px-3 py-2 text-sm text-brand-deep">
            {notice}
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            className="w-full rounded-lg border border-brand-deep/15 bg-white px-4 py-2.5 text-sm text-brand-deep focus:border-brand-accent focus:outline-none"
          />
          {mode !== "forgot" && (
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              className="w-full rounded-lg border border-brand-deep/15 bg-white px-4 py-2.5 text-sm text-brand-deep focus:border-brand-accent focus:outline-none"
            />
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-brand-accent px-5 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
          >
            {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
          </button>
        </form>

        {mode === "signin" && (
          <p className="mt-3 text-center text-sm">
            <button
              type="button"
              onClick={() => {
                setMode("forgot");
                setError(null);
                setNotice(null);
              }}
              className="font-semibold text-brand-accent hover:underline"
            >
              Forgot password?
            </button>
          </p>
        )}

        {mode !== "forgot" && (
          <>
            <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-brand-deep/80">
              <span className="h-px flex-1 bg-brand-deep/10" />
              or
              <span className="h-px flex-1 bg-brand-deep/10" />
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              disabled={busy}
              className="w-full rounded-full border border-brand-deep/15 bg-white px-5 py-2.5 text-sm font-semibold text-brand-deep transition-colors hover:bg-brand-muted/60 disabled:opacity-50"
            >
              Continue with Google
            </button>

            <button
              type="button"
              onClick={handleMicrosoft}
              disabled={busy}
              className="mt-3 w-full rounded-full border border-brand-deep/15 bg-white px-5 py-2.5 text-sm font-semibold text-brand-deep transition-colors hover:bg-brand-muted/60 disabled:opacity-50"
            >
              Continue with Microsoft
            </button>

            <button
              type="button"
              onClick={handleLovable}
              disabled={busy}
              className="lovable-auth-button mt-3 w-full"
            >
              Continue with Lovable
            </button>
          </>
        )}

        <p className="mt-6 text-center text-sm text-brand-deep/60">
          {mode === "forgot" ? "Remembered it?" : mode === "signin" ? "New to MG Content Labs?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError(null);
              setNotice(null);
            }}
            className="font-semibold text-brand-accent hover:underline"
          >
            {mode === "signin" ? "Create an account" : mode === "signup" ? "Sign in" : "Back to sign in"}
          </button>
        </p>
      </div>
    </main>
  );
}
